import { useEffect, useRef, useState } from 'react';

import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Chat = {
  id: string;
  message: string;
  reply: { answer: string; citations: { page: number; quote: string }[]; tool: string; note: string };
  source: { documentId: string; revisionId?: string; pageNumber?: number };
  trace: string[];
};
type Plan = { operation: 'coverage' | 'prepare_documents' | 'clarify'; hours: number; explanation: string };
type Turn = {
  chat?: Chat;
  id: string;
  request: string;
  response?: string;
  plan?: Plan;
  started?: boolean;
  error?: string;
};

/** Conversation is scoped to this mounted reviewer/org; never put clinical text in browser storage. */
export function AssistantConversation({
  base,
  runId,
  onReview,
  onChanged,
  onOpenSource,
}: {
  runId?: string | null;
  onOpenSource?: (source: { documentId: string; revisionId?: string; pageNumber?: number }) => void;
  base: string;
  onChanged: () => void;
  onReview?: () => void;
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [message, setMessage] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const lifetime = useRef<AbortController | null>(null);
  const inFlight = useRef(false);
  useEffect(() => {
    const abort = new AbortController();
    lifetime.current = abort;
    setEnabled(null);
    setLoadingHistory(!!runId);
    setBusy(false);
    inFlight.current = false;
    setTurns([]);
    setMessage('');
    void clientApi
      .get<{ planner_enabled?: boolean }>(`${base.replace('/workbench', '')}/capabilities`, { signal: abort.signal })
      .then(({ data }) => {
        if (!abort.signal.aborted) setEnabled(!!data.planner_enabled);
      })
      .catch(() => {
        if (!abort.signal.aborted) setEnabled(false);
      });
    if (runId)
      void clientApi
        .get<Chat[]>(`${base}/runs/${runId}/record/conversation`, { signal: abort.signal })
        .then(({ data }) => {
          if (!abort.signal.aborted && Array.isArray(data))
            setTurns(data.map((chat) => ({ chat, id: chat.id, request: chat.message, response: chat.reply.answer })));
        })
        .catch(() => {
          if (!abort.signal.aborted)
            setTurns([
              {
                request: '',
                id: 'history-error',
                error: 'Saved conversation could not be loaded. The selected record may still be preparing.',
              },
            ]);
        })
        .finally(() => {
          if (!abort.signal.aborted) setLoadingHistory(false);
        });
    return () => abort.abort();
  }, [base, runId]);
  function update(id: string, patch: Partial<Turn>, signal?: AbortSignal) {
    if (!signal?.aborted) setTurns((old) => old.map((turn) => (turn.id === id ? { ...turn, ...patch } : turn)));
  }
  async function send() {
    if (!enabled || loadingHistory || inFlight.current || !message.trim()) return;
    const signal = lifetime.current?.signal;
    const turn = { id: crypto.randomUUID(), request: message.trim() };
    inFlight.current = true;
    setBusy(true);
    setMessage('');
    setTurns((old) => [...old, turn]);
    try {
      if (runId) {
        const { data } = await clientApi.post<Chat>(
          `${base}/runs/${runId}/record/conversation`,
          { request_id: turn.id, message: turn.request },
          { signal },
        );
        update(turn.id, { chat: data, response: data.reply.answer }, signal);
        return;
      }
      const { data } = await clientApi.post<Plan>(
        `${base}/plan`,
        { request_id: turn.id, message: turn.request },
        { signal },
      );
      let response = data.explanation;
      if (data.operation === 'coverage') {
        const { data: report } = await clientApi.get<{
          total: number;
          status: string;
          counts: Record<string, number>;
        } | null>(`${base}/sweeps/latest`, { signal });
        response += report
          ? ` Latest scan: ${report.total} documents inventoried; ${Object.entries(report.counts)
              .map(([state, count]) => `${count} ${state.replaceAll('_', ' ')}`)
              .join(
                ', ',
              )}. ${report.status === 'scanning' ? 'Scan still running.' : 'Inventory status: ' + report.status + '.'} Inventory completion does not mean all clinical review is complete.`
          : ' No catch-up scan is available yet.';
      }
      update(turn.id, { response, plan: data }, signal);
    } catch {
      update(
        turn.id,
        {
          error:
            'The conversational planner is unavailable. Use Prepare work for direct catch-up and Decisions for review.',
        },
        signal,
      );
    } finally {
      if (!signal?.aborted) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }
  async function prepare(turn: Turn) {
    if (!turn.plan || inFlight.current || turn.started) return;
    const signal = lifetime.current?.signal;
    inFlight.current = true;
    setBusy(true);
    // Disable this request after any submission: a network timeout is not proof it failed.
    update(turn.id, { started: true }, signal);
    try {
      await clientApi.post(`${base}/sweeps`, { request_id: turn.id, hours: turn.plan.hours }, { signal });
      update(
        turn.id,
        {
          response:
            'Preparation requested. Progress and review tasks will appear in Decisions and Prepare work. No chart changes or patient messages have been approved.',
        },
        signal,
      );
      if (!signal?.aborted) onChanged();
    } catch {
      update(
        turn.id,
        {
          error:
            'Preparation was not confirmed. Check coverage in Prepare work before retrying; a scan may already be running.',
        },
        signal,
      );
    } finally {
      if (!signal?.aborted) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }
  async function confirmTool(turn: Turn) {
    if (!runId || !turn.chat || inFlight.current) return;
    setBusy(true);
    inFlight.current = true;
    const signal = lifetime.current?.signal;
    try {
      if (turn.chat.reply.tool === 'retry') {
        const { data } = await clientApi.get<{ version: number; hash: string }>(`${base}/runs/${runId}/record`, {
          signal,
        });
        await clientApi.post(
          `${base}/runs/${runId}/record/retry`,
          { expected_hash: data.hash, expected_version: data.version },
          { signal },
        );
      } else if (turn.chat.reply.tool === 'follow_up') {
        await clientApi.post(
          `${base}/runs/${runId}/follow-up`,
          { request_id: turn.id, note: turn.chat.reply.note },
          { signal },
        );
      }
      update(
        turn.id,
        {
          started: true,
          response:
            turn.chat.reply.tool === 'retry'
              ? 'Retry requested. Check the selected card for progress.'
              : 'Follow-up saved to the patient checklist.',
        },
        signal,
      );
      if (!signal?.aborted) onChanged();
    } catch {
      update(turn.id, { error: 'Action was not confirmed. Refresh the record before retrying.' }, signal);
    } finally {
      if (!signal?.aborted) {
        setBusy(false);
        inFlight.current = false;
      }
    }
  }
  async function clearHistory() {
    if (!runId || inFlight.current) return;
    const signal = lifetime.current?.signal;
    inFlight.current = true;
    setBusy(true);
    try {
      await clientApi.delete(`${base}/runs/${runId}/record/conversation`, { signal });
      if (!signal?.aborted) setTurns([]);
    } catch {
      if (!signal?.aborted)
        setTurns((old) => [
          ...old,
          { request: '', id: crypto.randomUUID(), error: 'Conversation could not be deleted.' },
        ]);
    } finally {
      if (!signal?.aborted) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }
  return (
    <Card aria-label="Conversation with Hermes">
      <CardHeader>
        <CardTitle>Ask the Assistant</CardTitle>
        <CardDescription>
          {runId
            ? 'Ask about the selected record, its source pages, proposed history or remaining processing issues. Clinical changes require review below.'
            : 'Hermes can plan document preparation and check coverage. Select a comprehensive record card to discuss its evidence.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {!!turns.length && (
          <div
            role="log"
            aria-live="polite"
            aria-label="Assistant conversation"
            className="flex max-h-64 flex-col gap-4 overflow-y-auto">
            {turns.map((turn) => (
              <section key={turn.id} className="flex flex-col gap-2">
                <p className="whitespace-pre-wrap break-words">
                  <strong>You:</strong> {turn.request}
                </p>
                {turn.response && (
                  <p className="whitespace-pre-wrap break-words">
                    <strong>Assistant:</strong> {turn.response}
                  </p>
                )}
                {!turn.response && !turn.error && <p>Planning…</p>}
                {turn.chat && (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Model-generated explanation · {turn.chat.trace.join(' → ')}
                    </p>
                    {turn.chat.reply.citations.map((citation, index) => (
                      <div key={index}>
                        <blockquote>{citation.quote}</blockquote>
                        <Button
                          variant="link"
                          onClick={() => onOpenSource?.({ ...turn.chat!.source, pageNumber: citation.page })}>
                          Source page {citation.page}
                        </Button>
                      </div>
                    ))}
                    {turn.chat.source.pageNumber && (
                      <Button variant="outline" onClick={() => onOpenSource?.(turn.chat!.source)}>
                        Open requested source page
                      </Button>
                    )}
                    {['follow_up', 'retry'].includes(turn.chat.reply.tool) && !turn.started && (
                      <>
                        {turn.chat.reply.tool === 'follow_up' && <p>Proposed follow-up: {turn.chat.reply.note}</p>}
                        <Button disabled={busy} onClick={() => void confirmTool(turn)}>
                          {turn.chat.reply.tool === 'retry'
                            ? 'Confirm retry of incomplete sections'
                            : 'Save proposed follow-up'}
                        </Button>
                      </>
                    )}
                    {turn.chat.reply.tool === 'review' && (
                      <Button variant="outline" onClick={onReview}>
                        Review proposed changes
                      </Button>
                    )}
                  </>
                )}
                {turn.error && <p role="alert">{turn.error}</p>}
                {turn.plan?.operation === 'prepare_documents' && !turn.started && (
                  <Button disabled={busy} onClick={() => void prepare(turn)}>
                    Start {turn.plan.hours}-hour preparation
                  </Button>
                )}
              </section>
            ))}
          </div>
        )}
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}>
          <Field>
            <FieldLabel htmlFor="assistant-conversation-request">Message Hermes</FieldLabel>
            <Textarea
              id="assistant-conversation-request"
              disabled={busy}
              value={message}
              maxLength={2000}
              placeholder="Prepare documents from the last 72 hours, or check preparation coverage"
              onChange={(event) => setMessage(event.target.value)}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy || loadingHistory || !enabled || !message.trim()}>
              {busy ? 'Working…' : 'Send request'}
            </Button>
            {onReview && (
              <Button type="button" variant="outline" onClick={onReview}>
                Review decisions
              </Button>
            )}
          </div>
        </form>
        {enabled === false && (
          <p role="status">
            Conversational planning is not activated or could not be reached. Direct catch-up and review remain
            available.
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          {runId
            ? 'This record conversation is saved for your account under the practice retention policy.'
            : 'General preparation conversation lasts while this sidebar is open.'}
        </p>
        {runId && (
          <Button variant="link" disabled={busy} onClick={() => void clearHistory()}>
            Delete this record conversation
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
